import { Component, OnInit } from '@angular/core';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { SmartIrrigationService, GeoAssetService } from 'src/api/services';
import { SmartIrrigationRootNodeDto, GeoAssetDetailDTO } from 'src/api/models';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { NodeTreeChangeEvent } from './node-tree-item/node-tree-item.component';

@Component({
  selector: 'app-smart-irrigation-administration',
  templateUrl: './administration.component.html',
  styleUrls: ['./administration.component.scss']
})
export class SmartIrrigationAdministrationComponent implements OnInit {

  constructor(
    private app: ApplicationConfigService,
    private irrigation: SmartIrrigationService,
    private geoAssetService: GeoAssetService
  ) {}

  rootNode: SmartIrrigationRootNodeDto | null = null;
  isLoading = false;
  loadError = false;
  geoAssets: GeoAssetDetailDTO[] = [];

  ngOnInit(): void {
    this.refreshTree();
    this.loadGeoAssets();
  }

  refreshTree(): void {
    this.isLoading = true;
    this.loadError = false;
    this.irrigation.SmartIrrigationGetRootNode(this.app.customerId).subscribe({
      next: (rootNode) => {
        this.rootNode = rootNode;
        this.isLoading = false;
      },
      error: (error) => {
        console.error('Error fetching root node:', error);
        this.isLoading = false;
        this.loadError = true;
      }
    });
  }

  loadGeoAssets(): void {
    this.geoAssetService.GeoAssetGetAllGeoAssetTypes(this.app.customerId).pipe(
      switchMap(types => {
        const irrigatableType = types.find(t => t.name === 'Irrigatable Asset');
        if (!irrigatableType) { return of([] as GeoAssetDetailDTO[]); }
        return this.geoAssetService.GeoAssetGetAllGeoAssets({
          customerId: this.app.customerId,
          assetTypeId: irrigatableType.id
        });
      })
    ).subscribe({
      next: assets => { this.geoAssets = assets; },
      error: err => { console.error('Failed to load geo assets:', err); }
    });
  }

  onNodeChanged(change: NodeTreeChangeEvent): void {
    if (change.type === 'move-node' && this.rootNode && change.newParentNodeId != null) {
      const moved = this.extractNode(this.rootNode, change.nodeId);
      const newParent = this.findNodeById(this.rootNode, change.newParentNodeId);
      if (moved && newParent) {
        moved.parentNodeId = change.newParentNodeId;
        newParent.childNodes = [...(newParent.childNodes || []), moved];
      }
    }
    // Other change types (e.g. delete-node) are already applied locally by the emitting node-tree-item.
  }

  private extractNode(root: SmartIrrigationRootNodeDto, nodeId: number): SmartIrrigationRootNodeDto | null {
    const children = root.childNodes || [];
    const index = children.findIndex(child => child.id === nodeId);
    if (index >= 0) {
      const [removed] = children.splice(index, 1);
      root.childNodes = [...children];
      return removed;
    }
    for (const child of children) {
      const found = this.extractNode(child, nodeId);
      if (found) {
        return found;
      }
    }
    return null;
  }

  private findNodeById(root: SmartIrrigationRootNodeDto, nodeId: number): SmartIrrigationRootNodeDto | null {
    if (root.id === nodeId) {
      return root;
    }
    for (const child of root.childNodes || []) {
      const found = this.findNodeById(child, nodeId);
      if (found) {
        return found;
      }
    }
    return null;
  }

}
